import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';

// Import contexts and util modules
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import {
  INQUIRY_PROCESS_NAME,
  isProcessWithoutPayment,
  isProcessWithPayment,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { userDisplayNameAsString } from '../../util/data';
import { isContentSubscriptionListing } from '../../util/digitalSubscription';
import { warmStripeWhenReady } from '../../util/stripeWarmup';

// Import global thunk functions
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';
import { confirmCardPayment, retrievePaymentIntent } from '../../ducks/stripe.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Import shared components
import { NamedRedirect, Page } from '../../components';

// Session helpers file needs to be imported before CheckoutPageWithPayment and CheckoutPageWithInquiryProcess
import {
  clearData,
  handlePageData,
  readInitialCheckoutPageData,
  storeData,
} from './CheckoutPageSessionHelpers';

// Import modules from this directory
import {
  clearCheckoutOrderErrors,
  confirmPayment,
  fetchCheckoutListing,
  initiateInquiryWithoutPayment,
  initiateOrder,
  sendMessage,
  setInitialValues,
  speculateTransaction,
  stripeCustomer,
  validateDiscount,
} from './CheckoutPage.duck';

import CheckoutPageWithInquiryProcess from './CheckoutPageWithInquiryProcess';
import CheckoutPageWithoutPayment, {
  loadInitialData as loadInitialDataWithoutPayment,
} from './CheckoutPageWithoutPayment';
import CheckoutPageWithPayment, {
  loadInitialDataForStripePayments,
} from './CheckoutPageWithPayment';
import CustomTopbar from './CustomTopbar';

const STORAGE_KEY = 'CheckoutPage';

if (typeof window !== 'undefined') {
  // Chunk evaluated on /checkout: start Stripe before the first React paint.
  warmStripeWhenReady(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
}

const onSubmitCallback = () => {
  clearData(STORAGE_KEY);
};

const getProcessName = pageData => {
  const { transaction, listing } = pageData || {};
  const processName = transaction?.id
    ? transaction?.attributes?.processName
    : listing?.id
    ? listing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0]
    : null;
  return resolveLatestProcessName(processName);
};

const EnhancedCheckoutPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const [pageData, setPageData] = useState(() =>
    readInitialCheckoutPageData(
      { orderData: props.orderData, listing: props.listing, transaction: props.transaction },
      STORAGE_KEY,
      history
    )
  );
  const [isDataLoaded, setIsDataLoaded] = useState(() => typeof window !== 'undefined');
  // Load Stripe customer + speculated transaction only once per checkout mount.
  // Re-running after initiateOrder writes transaction.id unmounts Stripe Elements
  // (loading spinner / BlankPage while speculate restarts) before confirmCardPayment
  // can read the card → "Element ... is mounted and the ready event has been emitted".
  const hasLoadedInitialDataRef = useRef(false);

  const listingId = props.listing?.id?.uuid;
  useEffect(() => {
    hasLoadedInitialDataRef.current = false;
  }, [listingId]);

  useEffect(() => {
    warmStripeWhenReady(
      config?.stripe?.publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    );
  }, [config?.stripe?.publishableKey]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const data =
      handlePageData(
        { orderData: props.orderData, listing: props.listing, transaction: props.transaction },
        STORAGE_KEY,
        history
      ) || {};
    setPageData(current => (getProcessName(current) ? current : data));
    setIsDataLoaded(true);
    warmStripeWhenReady(
      config?.stripe?.publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    );
  }, [props.listing?.id?.uuid, history]);

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) {
      return;
    }

    const { fetchSpeculatedTransaction, fetchStripeCustomer } = props;
    const data = pageData;
    const process = getProcessName(data);
    const subscriptionCheckout = isContentSubscriptionListing(data?.listing);
    // Subscription checkouts charge via Stripe Billing, not a Flex PaymentIntent.
    if (subscriptionCheckout) {
      fetchStripeCustomer();
      hasLoadedInitialDataRef.current = true;
    } else if (isProcessWithPayment(process)) {
      // Fetch StripeCustomer and speculateTransition for transactions that include Stripe payments
      loadInitialDataForStripePayments({
        pageData: data || {},
        fetchSpeculatedTransaction,
        fetchStripeCustomer,
        config,
      });
      hasLoadedInitialDataRef.current = true;
    } else if (isProcessWithoutPayment(process)) {
      loadInitialDataWithoutPayment({
        pageData: data || {},
        fetchSpeculatedTransaction,
        config,
      });
      hasLoadedInitialDataRef.current = true;
    }
    // Intentionally omit props.transaction?.id?.uuid — see hasLoadedInitialDataRef note above.
    // (react-hooks/exhaustive-deps is not enabled in this repo's eslint config.)
  }, [pageData, isDataLoaded, props.listing?.id?.uuid, props.orderData, config, history]);

  const { params, scrollingDisabled, onInquiryWithoutPayment } = props;
  const processName = getProcessName(pageData);
  const isInquiryProcess = processName === INQUIRY_PROCESS_NAME;

  // Handle redirection to ListingPage if required checkout data is not available.
  const listing = pageData?.listing;
  const hasRequiredData = !!(listing?.id && listing?.author?.id && processName);
  const shouldRedirect = isDataLoaded && !hasRequiredData;

  // Redirect back to ListingPage if data is missing.
  // Redirection must happen before any data format error is thrown (e.g. wrong currency)
  if (shouldRedirect) {
    // eslint-disable-next-line no-console
    console.error('Missing or invalid data for checkout, redirecting back to listing page.', {
      listing,
    });
    return <NamedRedirect name="ListingPage" params={params} />;
  }

  const listingTitle = listing?.attributes?.title;
  const authorDisplayName = userDisplayNameAsString(listing?.author, '');
  const title = processName
    ? intl.formatMessage(
        { id: `CheckoutPage.${processName}.title` },
        { listingTitle, authorDisplayName }
      )
    : 'Checkout page is loading data';

  const CheckoutWithInquiryComponent = () => (
    <CheckoutPageWithInquiryProcess
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      processName={processName}
      pageData={pageData}
      listingTitle={listingTitle}
      title={title}
      onInquiryWithoutPayment={onInquiryWithoutPayment}
      onSubmitCallback={onSubmitCallback}
      {...props}
    />
  );

  const CheckoutWithPaymentComponent = () => (
    <CheckoutPageWithPayment
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      processName={processName}
      sessionStorageKey={STORAGE_KEY}
      pageData={pageData}
      setPageData={setPageData}
      listingTitle={listingTitle}
      title={title}
      onSubmitCallback={onSubmitCallback}
      {...props}
    />
  );

  const BlankPage = (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <CustomTopbar intl={intl} linkToExternalSite={config?.topbar?.logoLink} />
    </Page>
  );

  const CheckoutWithoutPaymentComponent = () => (
    <CheckoutPageWithoutPayment
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      processName={processName}
      sessionStorageKey={STORAGE_KEY}
      pageData={pageData}
      setPageData={setPageData}
      listingTitle={listingTitle}
      title={title}
      onSubmitCallback={onSubmitCallback}
      {...props}
    />
  );

  if (!processName) {
    return BlankPage;
  }

  if (isProcessWithoutPayment(processName)) {
    return CheckoutWithoutPaymentComponent();
  }

  if (isInquiryProcess) {
    return CheckoutWithInquiryComponent();
  }

  // Mount checkout as soon as the process is known so the Stripe card
  // can render immediately. Speculate / price may load in parallel;
  // Confirm stays disabled until the matching quote is in.
  return CheckoutWithPaymentComponent();
};

const mapStateToProps = state => {
  const {
    listing,
    orderData,
    stripeCustomerFetched,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    isClockInSync,
    transaction,
    initiateInquiryError,
    initiateOrderError,
    confirmPaymentError,
    validateDiscountInProgress,
    validateDiscountError,
    appliedDiscount,
  } = state.CheckoutPage;
  const { currentUser } = state.user;
  const { confirmCardPaymentError, paymentIntent, retrievePaymentIntentError } = state.stripe;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    stripeCustomerFetched,
    orderData,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    isClockInSync,
    transaction,
    listing,
    initiateInquiryError,
    initiateOrderError,
    confirmCardPaymentError,
    confirmPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
    validateDiscountInProgress,
    validateDiscountError,
    appliedDiscount,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  fetchSpeculatedTransaction: (params, processAlias, txId, transitionName, isPrivileged) =>
    dispatch(speculateTransaction(params, processAlias, txId, transitionName, isPrivileged)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onInquiryWithoutPayment: (params, processAlias, transitionName) =>
    dispatch(initiateInquiryWithoutPayment(params, processAlias, transitionName)),
  onInitiateOrder: (params, processAlias, transactionId, transitionName, isPrivileged) =>
    dispatch(initiateOrder(params, processAlias, transactionId, transitionName, isPrivileged)),
  onRetrievePaymentIntent: params => dispatch(retrievePaymentIntent(params)),
  onConfirmCardPayment: params => dispatch(confirmCardPayment(params)),
  onConfirmPayment: (transactionId, transitionName, transitionParams) =>
    dispatch(confirmPayment(transactionId, transitionName, transitionParams)),
  onSendMessage: params => dispatch(sendMessage(params)),
  onSavePaymentMethod: (stripeCustomer, stripePaymentMethodId) =>
    dispatch(savePaymentMethod(stripeCustomer, stripePaymentMethodId)),
  onValidateDiscount: (listingId, userTryOnDiscountCode, options) =>
    dispatch(validateDiscount(listingId, userTryOnDiscountCode, options)),
  onClearCheckoutOrderErrors: () => dispatch(clearCheckoutOrderErrors()),
  onRefreshCheckoutListing: listingId => dispatch(fetchCheckoutListing(listingId)),
});

const CheckoutPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedCheckoutPage);

CheckoutPage.setInitialValues = (initialValues, saveToSessionStorage = false) => {
  if (saveToSessionStorage) {
    const { listing, orderData } = initialValues;
    storeData(orderData, listing, null, STORAGE_KEY);
  }

  return setInitialValues(initialValues);
};

CheckoutPage.displayName = 'CheckoutPage';

export default CheckoutPage;
